"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { motion } from "framer-motion"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"
import type { SheetMotionProps } from "./sheet.types"

const Sheet = SheetPrimitive.Root
const SheetTrigger = SheetPrimitive.Trigger
const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal
const SheetMotionContent = React.forwardRef<HTMLDivElement, SheetMotionProps>(
  ({ forceMount, onOpenAutoFocus, onCloseAutoFocus, ...props }, ref) => (
    <SheetPrimitive.Content forceMount={forceMount} onOpenAutoFocus={onOpenAutoFocus} onCloseAutoFocus={onCloseAutoFocus} asChild>
      <motion.div ref={ref} {...props} />
    </SheetPrimitive.Content>
  )
)
SheetMotionContent.displayName = "SheetMotionContent"

const SheetMotionOverlay = React.forwardRef<HTMLDivElement, SheetMotionProps>(
  ({ forceMount, ...props }, ref) => (
    <SheetPrimitive.Overlay forceMount={forceMount} asChild>
      <motion.div ref={ref} {...props} />
    </SheetPrimitive.Overlay>
  )
)
SheetMotionOverlay.displayName = "SheetMotionOverlay"

type SheetOverlayProps = React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay> & {
  position?: "fixed" | "absolute"
}

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  SheetOverlayProps
>(({ className, position = "fixed", ...props }, ref) => {
  const positionClass = position === "absolute" ? "absolute" : "fixed"

  return (
    <SheetPrimitive.Overlay
      ref={ref}
      className={cn(
        "inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-200",
        positionClass,
        className
      )}
      {...props}
    />
  )
})
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "z-50 bg-white shadow-[0_-12px_76px_rgba(0,0,0,0.16)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom rounded-t-3xl",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> &
  VariantProps<typeof sheetVariants> & {
    overlayClassName?: string
    portalContainer?: HTMLElement | null
  }
>(({ side = "right", className, children, overlayClassName, portalContainer, ...props }, ref) => {
  const [resolvedContainer, setResolvedContainer] = React.useState<HTMLElement | null | undefined>(
    portalContainer
  )

  React.useEffect(() => {
    setResolvedContainer(portalContainer)
  }, [portalContainer])

  const useAbsolute = Boolean(resolvedContainer)
  const positionClass = useAbsolute ? "absolute" : "fixed"

  return (
    <SheetPortal container={resolvedContainer ?? undefined}>
      <SheetOverlay className={overlayClassName} position={useAbsolute ? "absolute" : "fixed"} />
      <SheetPrimitive.Content
        ref={ref}
        className={cn(sheetVariants({ side }), positionClass, className)}
        {...props}
      >
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
})
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-2 text-center sm:text-left", className)} {...props} />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-[#1F2933]", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-[#6B7280]", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  SheetMotionContent,
  SheetMotionOverlay,
}
